<template>
	<div class="reservation-manage">
		<ModalHeader :title="customerName" />
		<div class="reservation-body">
			<p class="section-title">{{ getString('manage_reservation') }}</p>
			<div class="reservation-section reservation-actions mb-10">
				<div class="buttons-container">
					<div class="button-reservation done">
						<div class="left-part">
							<div class="action-color grey"></div>
							<div class="action-text">{{ getString('confirm') }}</div>
						</div>
						<div class="right-part" v-html="svgStore.get('iconCircleV')"></div>
					</div>
					<div class="button-reservation" :class="{loading, done: assignTableDone}" @click="clickAssignTables">
						<div class="left-part">
							<div class="action-color green"></div>
							<div class="action-text">{{ getString('assign_table') }}</div>
						</div>
						<div class="right-part" v-html="svgStore.get('iconCircleV')"></div>
					</div>
					<div class="button-reservation" v-if="isArrivedIf" :class="{loading, done: reservation && ['arrived', 'finished'].includes(reservation.status)}" @click.stop="updateStatus('arrived')">
						<div class="left-part">
							<div class="action-color yellow"></div>
							<div class="action-text">{{ getString('arrived') }}</div>
						</div>
						<div class="right-part" v-html="svgStore.get('iconCircleV')"></div>
					</div>
					<div class="button-reservation" v-if="isNoShowIf" :class="{loading, done: reservation && reservation.status === 'noshow'}" @click.stop="confirmPopup = 'noshow';$refs['res-confirm-popup'].toggleStatus(true)">
						<div class="left-part">
							<div class="action-color orange"></div>
							<div class="action-text">{{ getString('no_show') }}</div>
						</div>
						<div class="right-part" v-html="svgStore.get('iconCircleV')"></div>
					</div>
					<div class="button-reservation" v-if="isCanceledIf" :class="{loading, done: reservation && reservation.status === 'canceled'}" @click.stop="confirmPopup = 'canceled';$refs['res-confirm-popup'].toggleStatus(true)">
						<div class="left-part">
							<div class="action-color danger"></div>
							<div class="action-text">{{ getString('cancel') }}</div>
						</div>
						<div class="right-part" v-html="svgStore.get('iconCircleV')"></div>
					</div>
					<div class="button-reservation" v-if="isFinishedIf" :class="{loading, done: reservation && reservation.status === 'finished'}" @click.stop="updateStatus('finished')">
						<div class="left-part">
							<div class="action-color blue"></div>
							<div class="action-text">{{ getString('finished') }}</div>
						</div>
						<div class="right-part" v-html="svgStore.get('iconCircleV')"></div>
					</div>
				</div>
			</div>
			<p class="section-title">{{ getString('reservation_details') }}</p>
			<div class="reservation-section reservation-details">
				<div class="main-details">
					<div class="left-column">
						<div class="item-detail" v-if="reservation && reservation.email">
							<p class="info-label">{{ getString('email') }}</p>
							<div class="email">{{ reservationEmail }}</div>
						</div>
						<div class="item-detail" v-if="reservation && reservation.phone">
							<p class="info-label">{{ getString('phone') }}</p>
							<div class="phone">{{ reservationPhone }}</div>
						</div>
						<div v-if="reservationChildren || reservationDogs" class="item-detail">
							<p class="info-label">{{ getString('other') }}</p>
							<div class="gap-item">
								<div class="children" v-if="reservationChildren">{{ reservationChildren }}</div>
								<div class="dogs" v-if="reservationDogs">{{ reservationDogs }}</div>
							</div>
						</div>
						<div v-if="reservation && reservation.special" class="item-detail">
							<p class="info-label">{{ getString('special_occasion') }}</p>
							<div class="gap-item">
								<div class="special">{{ getString(reservation.special) }}</div>
							</div>
						</div>
						<div  v-if="reservation && reservation.allergensList.length > 0"  class="item-detail item-detail-100">
							<p class="info-label">{{ getString('allergens') }}</p>
							<div class="allergens">
								<div class="allergen-icon" v-for="allergen in reservation.allergensList" :key="allergen.allergen_id">
									<img :src="getAllergenIconUrl(allergen)" :alt="getString(allergen.name_key)">
								</div>
							</div>
						</div>
						<div v-if="reservation && reservation.amount_charged" class="item-detail">
							<p class="info-label">{{ getString('amount_charged') }}</p>
							<div class="charge">{{ currencyStore.readableCurrencyValue(reservation.amount_charged) }}</div>
						</div>
						<div class="item-detail">
							<p class="info-label">{{ getString('experience') }}</p>
							<div class="creation-type">{{ experienceName }}</div>
						</div>

						<div v-if="reservation && reservation.creation_type" class="item-detail">
							<p class="info-label">{{ getString('booking_origin') }}</p>
							<div class="creation-type">{{ getString(reservation.creation_type) }}</div>
						</div>

						<div class="item-detail" v-if="reservation && reservation.notes">
							<p class="info-label">{{ getString('notes') }}</p>
							<div class="notes">{{ reservationNotes }}</div>
						</div>

						<div class="item-detail" v-if="reservation && reservation.custom_answer">
							<p class="info-label">{{ getString('answer_to_the_question') }}</p>
							<div class="notes">{{ reservationCustomAnswer }}</div>
						</div>
					</div>
					<div class="right-column">
						<div class="double-info time-row">
							<div class="first-info">
								<p class="info-label">{{ getString('time') }}</p>
								<p class="info-value first">
									<span class="value-content">{{ reservationTime }}</span>
									<span class="value-icon" v-html="svgStore.get('iconClock')"></span>
								</p>
							</div>
							<div class="second-info">
								<p class="info-label">{{ getString('people') }}</p>
								<p class="info-value">
									<span class="value-content">{{ reservation ? reservation.people : 0 }}</span>
									<span class="value-icon" v-html="svgStore.get('iconUser')"></span>
								</p>
							</div>
						</div>
						<div v-if="reservation?.tablesRoom?.length" class="divider"></div>
						<div class="double-info tables-row" v-for="table in (reservation ? reservation.tablesRoom : [])">
							<div class="first-info">
								<p class="info-label">{{ getString('position_room') }}</p>
								<div class="row">
									<p class="info-value first">
										<span class="value-content">{{ table.table_name }}</span>
										<span class="value-icon" v-html="svgStore.get('iconAnalyticsTable')"></span>
									</p>
									<p class="info-value first">
										<span class="value-content">{{ table.room_name }}</span>
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="divider"></div>
				<div class="buttons-container-edit">
					<AppButton v-if="reservation && reservation.status === 'confirmed'" class="mr-10" :loading="printingReservation" :text="getString('print')" outline @click="printReservation"/>
					<AppButton outline :text="getString('edit')" @click="roomsOrdersStore.editReservationId = roomsOrdersStore.manageReservationId" />
				</div>
			</div>
		</div>

		<AppPopup
			:title="getString('are_you_sure')"
			:body-text="popupStatusBody"
			:buttons="popupStatusButtons"
			ref="res-confirm-popup"
		/>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useRoomsOrdersStore, useRoomsStore, useExperiencesStore, useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import AppButton from "@/shared/components/AppButton.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import API from "@/shared/helpers/API.js";

export default {
	setup() {
		const roomsOrdersStore = useRoomsOrdersStore();
		const roomsStore = useRoomsStore();
		const experiencesStore = useExperiencesStore();
		const languageStore = useLanguageStore();
		const svgStore = useSvgStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		return {
			userStore,
			roomsOrdersStore,
			roomsStore,
			svgStore,
			currencyStore,
			experiencesStore,
			notificationStore,
			getString: languageStore.getString
		}
	},
	name: "ReservationManage",
	components: {AppPopup, ModalHeader, AppButton},
	data() {
		return {
			confirmPopup: null,
			loading: false,
			printingReservation: false
		}
	},
	computed: {
		popupStatusButtons() {
			if(!this.reservation) {
				return [];
			}
			if(this.confirmPopup === "noshow") {
				return [
					{text: this.getString('cancel'), callback: null, negative: true},
					{text: this.getString('confirm'), callback: () => {
							this.updateStatus('noshow');
							this.$refs['res-confirm-popup'].toggleStatus(false);
						}},
				]
			} else if(this.confirmPopup === "canceled") {
				const withPayment = this.reservation.payment_mode === "payment" && this.reservation.amount_charged > 0;
				if(withPayment) {
					return [
						{text: this.getString('cancel'), callback: null, negative: true},
						{text: this.getString('no_refund'), negative: true, callback: () => {
								this.updateStatus('canceled');
								this.$refs['res-confirm-popup'].toggleStatus(false);
							}},
						{text: this.getString('refund'), callback: () => {
								this.updateStatus('canceled', true);
								this.$refs['res-confirm-popup'].toggleStatus(false);
							}},
					]
				} else {
					return [
						{text: this.getString('cancel'), callback: null, negative: true},
						{text: this.getString('confirm'), callback: () => {
								this.updateStatus('canceled');
								this.$refs['res-confirm-popup'].toggleStatus(false);
							}},
					]
				}
			} else {
				return [];
			}
		},
		popupStatusBody() {
			if(!this.reservation) {
				return '';
			}
			if(this.confirmPopup === "noshow") {
				const withPayment = this.reservation.payment_mode !== null;
				return this.getString('confirm_no_show')+(withPayment ? " "+this.getString('confirm_no_show_payment') : '');
			} else if(this.confirmPopup === "canceled") {
				const withPayment = this.reservation.payment_mode === "payment" && this.reservation.amount_charged > 0;
				return this.getString('confirm_canceled')+(withPayment ? " "+this.getString('confirm_canceled_payment') : '');
			} else {
				return '';
			}
		},
		reservation() {
			return this.roomsOrdersStore.manageReservationId ? this.roomsOrdersStore.getReservationById(this.roomsOrdersStore.manageReservationId) : null;
		},
		currentExperience() {
			if(this.reservation && this.reservation?.experience_id) {
				return  this.experiencesStore.getExperienceById(this.reservation.experience_id);
			}
			return null;
		},
		experienceName() {
			return this.currentExperience ? this.currentExperience.name : null;
		},
		customerName() {
			return this.reservation ? this.reservation.name : "";
		},
		reservationEmail() {
			return this.reservation ? this.reservation.email : "";
		},
		reservationPhone() {
			return this.reservation ? this.reservation.phone : "";
		},
		reservationNotes() {
			return this.reservation ? this.reservation.notes : "";
		},
		reservationCustomAnswer() {
			return this.reservation ? this.reservation.custom_answer : "";
		},
		reservationDogs() {
			return this.reservation && this.reservation.dogs ? "x"+this.reservation.dogs+" "+this.getString("dogs") : null;
		},
		reservationChildren() {
			return this.reservation && this.reservation.children ? "x"+this.reservation.children+" "+this.getString("children") : null;
		},
		reservationTime() {
			return this.reservation ? this.reservation.timeDisplay : "";
		},
		assignTableDone() {
			return this.reservation && this.reservation.tablesRoom && this.reservation.tablesRoom.length > 0;
		},
		isArrivedIf() {
			return this.reservation && ["confirmed", "arrived", "finished"].includes(this.reservation.status) && (this.reservation.fullDate - new Date()) < 86400 * 1000;
		},
		isNoShowIf() {
			return this.reservation && ["confirmed", "noshow"].includes(this.reservation.status) && (this.reservation.fullDate - new Date()) < 86400 * 1000;
		},
		isCanceledIf() {
			return this.reservation && ["confirmed", "canceled"].includes(this.reservation.status);
		},
		isFinishedIf() {
			return this.reservation && ["arrived", "finished"].includes(this.reservation.status);
		},
	},
	methods: {
		getAllergenIconUrl(allergen) {
			const iconName = allergen.name_key.replace('allergens_', '');
			const primaryColorRgb = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();
			
			// Convert RGB to hex
			let primaryColor = primaryColorRgb;
			if (primaryColorRgb.startsWith('rgb')) {
				const rgbMatch = primaryColorRgb.match(/\d+/g);
				if (rgbMatch && rgbMatch.length >= 3) {
					const r = parseInt(rgbMatch[0]).toString(16).padStart(2, '0');
					const g = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
					const b = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
					primaryColor = r + g + b;
				}
			} else {
				primaryColor = primaryColorRgb.replace('#', '');
			}
			
			return `${URL_HTML}/img/allergens/icon-${iconName}.php?c=${primaryColor}`;
		},
		clickAssignTables() {
			if(this.reservation && !this.assignTableDone) {
				this.roomsOrdersStore.editReservationId = this.reservation.reservation_id;
				setTimeout(() => {
					this.roomsOrdersStore.editReservationTables = true;
				}, 200);
			}
		},
		updateStatus(status, refund = false) {
			this.loading = true;
			this.roomsOrdersStore.updateReservationStatus(this.reservation.reservation_id, status, refund, (r) => {
				if(r && r.result && r.result === "OK") {
					if(this.reservation) {
						this.reservation.status = status;
					}
					if(["canceled", "noshow", "finished"].includes(status)) {
						this.roomsOrdersStore.manageReservationId = null;
					}
					if(this.roomsOrdersStore.reservationsTablesActive) {
						this.roomsOrdersStore.getDayReservations(() => {
							this.loading = false;
						});
					} else {
						this.roomsStore.updateRoomStatus(this.roomsStore.activeRoom, () => {
							this.loading = false;
						});
					}
				} else {
					this.notificationStore.showNotification("generic-error");
					this.loading = false;
				}
			});
		},
		printReservation() {
			this.printingReservation = true;
			API.init().printReservation(this.userStore.currBusinessId, this.roomsOrdersStore.manageReservationId, (r) => {
				this.printingReservation = false;
				if(r && r.result && r.result === "OK") {
					this.notificationStore.showNotification("print-reservation-success");
				}
				else if (r && r.details && r.details === "no_printers_found") {
					this.notificationStore.showNotification("no-printers-found");
				}
				else {
					this.notificationStore.showNotification("generic-error");
				}
			});

		}
	}
}
</script>

<style scoped lang="scss">
.reservation-manage {

	.reservation-body {
		display: flex;
		flex-direction: column;
		gap: 12px;

		.section-title {
			font-size: 18px;
			font-weight: 500;
			margin-bottom: 4px;
		}

		.reservation-details {
			background-color: var(--color-bg-primary-2);
			border-radius: 13px;
			display: flex;
			flex-direction: column;

			.main-details {
				display: flex;
				justify-content: space-between;
				padding: 12px;

				@media (max-width: 768px) {
					flex-direction: column-reverse;
					gap: 12px;
				}
			}

			.left-column {
				display: flex;
				flex-wrap: wrap;
				gap: 12px;
				width: 70%;
				border-right: 1px solid var(--color-primary-alpha-4);
				padding-right: 12px;
				@media (max-width: 768px) {
					width: 100%;
					border: none;
					padding-right: 0;
				}
				.allergens {
					display: flex;
					flex-direction: row;
					align-items: center;
					flex-wrap: wrap;
					width: 100%;
					gap: 8px;
					padding: 0 2px;

					.allergen-icon {
						display: flex;
						position: relative;
						justify-content: center;
						align-items: center;

						&:before {
							content: "";
							position: absolute;
							top: -2.25px;
							left: -2.25px;
							width: 22px;
							height: 22px;
							border-radius: 13px;
							border: 1px solid var(--color-primary);
							z-index: 1;
						}

						img {
							width: 19px;
							height: 19px;
							object-fit: contain;
							position: relative;
							z-index: 2;
						}
					}
				}
				.row {
					display:  flex;
					gap: 10px;
				}
				.item-detail {
					display: flex;
					flex-direction: column;
					min-width: calc(50% - 8px);

					.info-label {
						font-size: 12px;
						color: var(--color-txt-1);
					}

					.email, .phone, .dogs, .children, .special, .charge, .creation-type {
						font-size: 16px;
						color: var(--color-text-primary);
						background-color: var(--color-bg-primary-1);
						border-radius: 13px;
						padding: 6px 10px;
						min-height: 35px;
						width: fit-content;
					}
					.gap-item {
						display: flex;
						gap: 8px;
						flex-wrap: wrap;
					}
					&-100 {
						width: 100%;
					}
				}
			}

			.right-column {
				display: flex;
				flex-direction: column;
				gap: 12px;
				width: 30%;
				align-items: flex-end;
				padding-left: 12px;
				@media (max-width: 768px) {
					width: 100%;
					padding-left: 0;
				}

				.double-info {
					display: flex;
					justify-content: space-between;
					gap: 8px;
					width:  100%;

					.info-label {
						font-size: 12px;
						color: var(--color-txt-1);
					}
					.info-value {
						display: flex;
						align-items: center;
						justify-content: space-between;
						font-size: 16px;
						color: var(--color-text-primary);
						background-color: var(--color-bg-primary-1);
						border-radius: 13px;
						padding: 6px 10px;
						min-height: 35px;
						gap: 4px;
						width: fit-content;

						&.first {
							width: fit-content;
						}

						.value-content {
							margin-right: 4px;
							font-size: 14px;
						}

						.value-icon {
							width: 16px;
							height: 16px;
							display: flex;
							align-items: center;
							justify-content: center;

							:deep(svg) {
								width: 16px;
								height: 16px;

								.fill {
									fill: var(--color-primary);
								}

								.stroke {
									stroke: var(--color-primary);
								}

							}
						}
					}
					.row {
						display: flex;
						gap: 8px;
					}
				}
			}
		}

		.divider {
			height: 1px;
			background-color: var(--color-primary-alpha-4);
			width: 100%;
		}

		.buttons-container-edit {
			display: flex;
			justify-content: flex-end;
			padding: 12px;
		}

		.buttons-container {
			display: flex;
			gap: 12px;
			flex-wrap: wrap;

			.button-reservation {
				display: flex;
				align-items: center;
				justify-content: space-between;
				background-color: var(--color-bg-primary-2);
				border: 1px solid var(--color-primary);
				border-radius: 13px;
				padding: 8px 12px;
				gap: 8px;
				width: calc((100% / 3) - 8px);
				cursor: pointer;
				transition: opacity 0.2s ease-in-out, background-color 0.2s ease-in-out, border 0.2s ease-in-out;

				&.loading {
					opacity: 0.5;
					pointer-events: none;
				}

				@media (max-width: 768px) {
					width: calc((100% / 2) - 8px);
				}

				@media (max-width: 360px) {
					width: 100%;
				}

				.left-part {
					display: flex;
					align-items: center;
					gap: 8px;

					.action-color {
						width: 10px;
						height: 10px;
						border-radius: 50%;

						&.blue {
							background-color: var(--color-primary);
						}

						&.green {
							background-color: var(--color-secondary);
						}

						&.yellow {
							background-color: #FDC700;
						}

						&.orange {
							background-color: #FC7502;
						}

						&.danger {
							background-color: var(--color-danger);
						}

						&.grey {
							background-color: var(--color-txt-2);
						}
					}

					.action-text {
						font-size: 15px;
						font-weight: 700;
						color: var(--color-text-primary);
					}
				}

				.right-part {
					width: 18px;
					height: 18px;
					align-items: center;
					justify-content: center;
					display: none;

					:deep(svg) {
						width: 18px;
						height: 18px;

						.fill {
							fill: var(--color-primary);
						}

						.stroke {
							stroke: var(--color-primary);
						}
					}
				}

				&.done {
					background-color: var(--color-bg-primary-1);
					border-color: transparent;

					.left-part {
						.action-color {
							background-color: var(--color-txt-2);
						}

						.action-text {
							color: var(--color-txt-2);
						}
					}

					.right-part {
						display: flex;

						:deep(svg) {
							.fill {
								fill: var(--color-txt-2);
							}

							.stroke {
								stroke: var(--color-txt-2);
							}
						}
					}
				}
			}
		}

	}

}
</style>